import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface RoleOption {
  role: string;
  permissions: string[];
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  superadmin: "Unrestricted access. Cannot be assigned by invite.",
  admin: "Full access to the console, including inviting and managing admins.",
  editor: "Create and edit DPPs, add events, and work through submissions.",
  salesperson: "View DPPs, submissions, and users. No editing.",
  viewer: "Read-only access across DPPs, submissions, users, and the audit log.",
};

const PERMISSION_LABELS: Record<string, string> = {
  "dpp:view": "View DPPs",
  "dpp:create": "Mint DPPs",
  "dpp:edit": "Edit DPPs",
  "dpp:event:add": "Add DPP events",
  "submission:view": "View submissions",
  "submission:update": "Update submissions",
  "user:view": "View users",
  "admin:view": "View admins",
  "admin:manage": "Manage admins",
  "audit:view": "View audit log",
};

export function InviteAdminForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [showRoles, setShowRoles] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ data: RoleOption[] }>("/admin/admins/roles")
      .then(({ data }) => {
        if (cancelled) return;
        setRoles(data.data);
        if (data.data[0]) setRole(data.data[0].role);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load roles");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/admin/admins", { email, role });
      setDone(true);
      onSuccess();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to send invite"));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
        Invite sent to <b>{email}</b>. They'll receive an email with a link to
        set their password.
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div className="field">
        <Label htmlFor="invite-email">Email</Label>
        <Input
          id="invite-email"
          type="email"
          required
          placeholder="person@watchcentral.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="field">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Label htmlFor="invite-role">Role</Label>
          <button
            type="button"
            onClick={() => setShowRoles((s) => !s)}
            aria-expanded={showRoles}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              cursor: "pointer",
              color: "var(--ink-mute)",
              fontFamily: "var(--f-mono)",
              fontSize: 10.5,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <InfoIcon />
            {showRoles ? "Hide definitions" : "What do roles mean?"}
          </button>
        </div>
        <select
          id="invite-role"
          className="select"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={submitting}
          style={{ padding: "10px 14px", fontSize: 13, appearance: "none" }}
        >
          {roles.map((r) => (
            <option key={r.role} value={r.role}>
              {r.role}
            </option>
          ))}
        </select>

        {showRoles && (
          <div
            style={{
              marginTop: 12,
              border: "1px solid var(--line)",
              background: "var(--surface-2)",
              padding: 14,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {roles.map((r) => (
              <div key={r.role}>
                <div
                  style={{
                    fontFamily: "var(--f-mono)",
                    fontSize: 12,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color:
                      r.role === role ? "var(--accent)" : "var(--ink)",
                  }}
                >
                  {r.role}
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: "var(--ink-soft)",
                    marginTop: 4,
                  }}
                >
                  {ROLE_DESCRIPTIONS[r.role] ?? ""}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 8,
                  }}
                >
                  {r.permissions.map((p) => (
                    <span
                      key={p}
                      style={{
                        border: "1px solid var(--line)",
                        padding: "3px 8px",
                        fontFamily: "var(--f-mono)",
                        fontSize: 10,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--ink-mute)",
                      }}
                    >
                      {PERMISSION_LABELS[p] ?? p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit" disabled={submitting || !role}>
        {submitting ? "Sending…" : "Send invite"}
      </Button>
    </form>
  );
}

function InfoIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12" y2="8" />
    </svg>
  );
}
